import { ReactNode } from 'react';
import { Minifiable } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { OptimusTextKey } from '@optimus-web/localization';
import { StackItem } from '../Stack';
import {
    StepButton,
    StepIconElement,
    StepNumberElement,
    StepPrimaryTextElement,
    StepSecondaryTextElement,
    StepTextWrapper,
    StepWrapper,
    StepInnerWrapper,
    StepCountElement,
} from './style';

export interface StepDescription<S extends string = string> {
    value: S;
    label: string | ReactNode;
    optional?: boolean;
    active?: boolean;
    completed?: boolean;
    editable?: boolean;
    order?: number;
    ariaLabel?: string;
}

export interface StepProps<S extends string = string> extends StepDescription<S>, Minifiable {
    index: number;
    onClick?: (value: S) => void;
    vertical?: boolean;
    singleItem?: boolean;
    isLastStep?: boolean;
    isPrimary?: boolean;
    nextIsVisited?: boolean;
    isVerticalOpenActiveStep?: boolean;
    stepsCount: number;
    isOpenable?: boolean;
    isOpened?: boolean;
}

export const Step = <S extends string = string>({
    index,
    label,
    ariaLabel,
    value,
    optional,
    completed,
    editable,
    vertical,
    active,
    small,
    singleItem,
    onClick,
    isLastStep = false,
    isPrimary = false,
    nextIsVisited = false,
    isVerticalOpenActiveStep = false,
    stepsCount,
    isOpenable,
    isOpened,
    ...other
}: StepProps<S>) => {
    const isClickable = !active && editable;

    const handleClick = () => {
        onClick?.(value);
    };

    return (
        <StepWrapper {...other} isLastStep={isLastStep} vertical={vertical}>
            <StepButton
                type="button"
                as={isClickable ? 'button' : 'div'}
                onClick={isClickable ? handleClick : undefined}
                active={active}
                completed={completed}
                clickable={isClickable}
                small={small}
                aria-current={active ? 'step' : undefined}
                data-test-step={value}
                aria-label={ariaLabel}
            >
                <StepInnerWrapper
                    vertical={vertical}
                    active={active}
                    completed={completed}
                    editable={editable}
                    isLastStep={isLastStep}
                    isPrimary={isPrimary}
                    nextIsVisited={nextIsVisited}
                    isOpenable={isOpenable}
                    isOpened={isOpened}
                >
                    {!completed || active ? (
                        <StepNumberElement
                            active={active}
                            completed={completed}
                            clickable={isClickable}
                            vertical={vertical}
                        >
                            {active || isClickable ? index + 1 : ''}
                        </StepNumberElement>
                    ) : (
                        <StepIconElement
                            value={!active && completed && IconName.Done}
                            primary={active || completed}
                            active={active}
                            completed={completed}
                            singleItem={singleItem}
                            size="small"
                            vertical={vertical}
                        />
                    )}
                    <StepTextWrapper vertical={vertical} isVerticalOpenActiveStep={isVerticalOpenActiveStep}>
                        <StepPrimaryTextElement
                            active={active}
                            clickable={isClickable}
                            completed={completed}
                            isVerticalOpenActiveStep={isVerticalOpenActiveStep}
                        >
                            <StackItem>{label}</StackItem>
                            {isVerticalOpenActiveStep ? (
                                <StepCountElement>
                                    {index + 1} {OptimusTextKey.Of.toLowerCase()} {stepsCount}
                                </StepCountElement>
                            ) : null}
                        </StepPrimaryTextElement>
                        {optional && <StepSecondaryTextElement>{OptimusTextKey.Optional}</StepSecondaryTextElement>}
                    </StepTextWrapper>
                </StepInnerWrapper>
            </StepButton>
        </StepWrapper>
    );
};
